import React from 'react';
import { MenuItem, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { US_STATES } from '../../utils/usStates';
import { CA_PROVINCES } from '../../utils/caProvinces';

export interface RegionSelectProps {
  country: 'US' | 'CA' | string;
  /** State or province code for US and CA; free text elsewhere. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

/** State (US), province (CA) or plain text (anywhere else). */
export const RegionSelect: React.FC<RegionSelectProps> = ({
  country,
  value,
  onChange,
  label,
  error,
  helperText,
  size = 'small',
  required,
  fullWidth,
  disabled,
  sx,
  'aria-label': ariaLabel,
}) => {
  const c = String(country || '').trim().toUpperCase();
  const list = c === 'US' ? US_STATES : c === 'CA' ? CA_PROVINCES : null;
  const resolvedLabel = label ?? (c === 'CA' ? 'Province' : c === 'US' ? 'State' : 'State / Region');

  if (!list) {
    return (
      <TextField
        size={size}
        required={required}
        fullWidth={fullWidth}
        disabled={disabled}
        sx={sx}
        label={resolvedLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        helperText={helperText}
        slotProps={{ htmlInput: { 'aria-label': ariaLabel ?? resolvedLabel } }}
      />
    );
  }

  const upper = value.trim().toUpperCase();
  // A stored full name ("California") resolves to its code; a value from the other country's list shows as empty.
  const byName = Object.entries(list).find(([, name]) => name.toUpperCase() === upper)?.[0];
  const current = list[upper] ? upper : byName ?? '';

  return (
    <TextField
      select
      size={size}
      required={required}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={sx}
      label={resolvedLabel}
      value={current}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={helperText}
      slotProps={ariaLabel ? { htmlInput: { 'aria-label': ariaLabel } } : undefined}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {Object.entries(list).map(([code, name]) => (
        <MenuItem key={code} value={code}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
};
